import { useState, useEffect, useCallback, useMemo } from 'react';
import type { ServerInfo, Player, ServerSettings, ServerMetrics, PlayerInfoRecord, MetricsRecord, PlayersOnlineRecord, UptimeStatus } from '../types';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

async function fetchApi<T>(endpoint: string): Promise<T> {
  const response = await fetch(`${API_BASE}${endpoint}`);
  if (!response.ok) {
    throw new Error(`API error: ${response.status}`);
  }
  return response.json();
}

export function useServerInfo() {
  const [data, setData] = useState<ServerInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchApi<ServerInfo>('/info');
      setData(result);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function usePlayers() {
  const [data, setData] = useState<Player[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchApi<{ players: Player[] }>('/players');
      setData(result.players);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useSettings() {
  const [data, setData] = useState<ServerSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchApi<ServerSettings>('/settings');
      setData(result);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useMetrics() {
  const [data, setData] = useState<ServerMetrics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchApi<ServerMetrics>('/metrics');
      setData(result);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

interface HistoryQuery {
  start_time?: string;
  end_time?: string;
  limit?: number;
  offset?: number;
  player_uid?: string;
}

function buildQuery(params: HistoryQuery): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  });
  return searchParams.toString();
}

export function useMetricsHistory(query: HistoryQuery = {}) {
  const [data, setData] = useState<MetricsRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Memoize query string to prevent re-fetching on object reference changes
  // Default to 2880 records (24 hours * 60 minutes * 2 per minute at 30s interval)
  const queryString = useMemo(() => buildQuery({ ...query, limit: query.limit || 2880 }), [query.start_time, query.end_time, query.limit, query.offset]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchApi<{ history: MetricsRecord[] }>(`/history/metrics?${queryString}`);
      setData(result.history);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function usePlayersOnlineHistory(query: HistoryQuery = {}) {
  const [data, setData] = useState<PlayersOnlineRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  // Default to 2880 records (24 hours at 30s interval)
  const queryString = useMemo(() => buildQuery({ ...query, limit: query.limit || 2880 }), [query.start_time, query.end_time, query.limit, query.offset]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchApi<{ history: PlayersOnlineRecord[] }>(`/history/players-online?${queryString}`);
      setData(result.history);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function usePlayerHistory(query: HistoryQuery = {}) {
  const [data, setData] = useState<PlayerInfoRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const queryString = useMemo(() => buildQuery({ ...query, limit: query.limit || 100 }), [query.start_time, query.end_time, query.limit, query.offset, query.player_uid]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchApi<{ history: PlayerInfoRecord[] }>(`/history/players?${queryString}`);
      setData(result.history);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// Player Sessions hooks
export interface PlayerSession {
  player_id: string;
  user_id: string;
  name: string;
  account_name?: string;
  join_time: string;
  leave_time: string;
  duration_seconds: number;
  duration_human: string;
  snapshots: number;
  max_level: number;
}

export function usePlayerSessions(query: HistoryQuery = {}) {
  const [data, setData] = useState<PlayerSession[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const queryString = useMemo(() => buildQuery({ ...query, limit: query.limit || 5000 }), [query.start_time, query.end_time, query.limit, query.offset, query.player_uid]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchApi<{ sessions: PlayerSession[] }>(`/sessions?${queryString}`);
      setData(result.sessions);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

export function useUptimeStatus(query: HistoryQuery = {}) {
  const [data, setData] = useState<UptimeStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const queryString = useMemo(() => buildQuery({ start_time: query.start_time }), [query.start_time]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const result = await fetchApi<UptimeStatus>(`/uptime?${queryString}`);
      setData(result);
      setError(null);
    } catch (err) {
      setError(err as Error);
    } finally {
      setLoading(false);
    }
  }, [queryString]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, [fetchData]);

  return { data, loading, error, refetch: fetchData };
}

// CSV Export helpers
export function downloadCSV(url: string, filename: string) {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

export function buildExportUrl(endpoint: string, params: Record<string, string | undefined>): string {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== '') {
      searchParams.append(key, value);
    }
  });
  return `${API_BASE}${endpoint}?${searchParams.toString()}`;
}